import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbInputDatepicker, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BookedLessonsService } from 'src/app/services/booked-lessons.service';
import { ActivatedRoute } from '@angular/router';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { BookedLessonAddModal } from '../booked-lessons-add-modal/booked-lesson-add-modal.component';
import { Page } from 'src/app/classes/Page';
import { FormBuilder, FormGroup } from '@angular/forms';
import { User } from 'src/app/classes/User';
import { UserService } from 'src/app/services/user/user.service';
import { CoursesService } from 'src/app/services/courses.service';
import { LessonsScheduleService } from 'src/app/services/lessons-schedule.service';
import { ConfirmDialogComponent } from 'src/app/components/dialog/confirm-dialog/confirm-dialog.component';
import {
  RescheduleBookedLessonComponent
} from 'src/app/components/booked-lessons/reschedule-booked-lesson/reschedule-booked-lesson.component';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-booked-lessons-list',
  templateUrl: './booked-lessons-list.component.html',
  styleUrls: ['./booked-lessons-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BookedLessonsListComponent implements OnInit {
  public isAdmin = false;
  public isSuperAdmin = false;
  public errorMessage: string;
  public teachersFormGroup: FormGroup;
  public bundlesFormGroup: FormGroup;
  public schedulesFormGroup: FormGroup;
  public teachers: User[];
  public courses = [];
  public schedules = [];
  public pageNumber = 1;
  public totalElements = 0;
  public pageSizes = [10, 20, 50];
  public pageSize = this.pageSizes[0];
  private radioModelValue = 'SINGLE';

  @ViewChild('datepicker', { static: false }) public datepicker: NgbInputDatepicker;

  public hoveredDate: NgbDate | null = null;
  public fromDate: NgbDate | null = null;
  public toDate: NgbDate | null = null;
  public selectedLessons = [];
  private currentDialog = null;
  public expandedRowIndex: number | null = null;
  private filters: any = {
    subscriptionType: `type=${this.radioModelValue}`
  };

  constructor(
    private bookedLessonsService: BookedLessonsService,
    private calendar: NgbCalendar,
    private route: ActivatedRoute,
    private localStorageService: LocalStorageService,
    private formatter: NgbDateParserFormatter,
    private modalService: NgbModal,
    private userService: UserService,
    private formBuilder: FormBuilder,
    private coursesService: CoursesService,
    private scheduleService: LessonsScheduleService,
  ) {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    this.fromDate = new NgbDate(monthStart.getFullYear(), monthStart.getMonth() + 1, monthStart.getDate());
    this.toDate = new NgbDate(monthEnd.getFullYear(), monthEnd.getMonth() + 1, monthEnd.getDate());
    this.teachersFormGroup = this.formBuilder.group({
      teacherId: [''],
    });
    this.bundlesFormGroup = this.formBuilder.group({
      bundleId: ['']
    });
    this.schedulesFormGroup = this.formBuilder.group({
      scheduleId: ['']
    });
  }

  ngOnInit(): void {
    this.route.data.subscribe(() => {
      const user = this.localStorageService.getUser();
      this.isAdmin = user.roles.includes('ROLE_ADMIN');
      this.isSuperAdmin = user.roles.includes('ROLE_SUPER_ADMIN');
    });
    if (this.fromDate && this.toDate) {
      this.checkRangeAndGetBookedLessons();
    }
    this.userService
      .getAllUsers(0, 10000, ['ROLE_ADMIN', 'ROLE_TEACHER'])
      .subscribe((value) => {
        this.teachers = [
          {
            id: '' as any,
            firstName: 'All',
            lastName: 'teachers'
          } as User,
          {
            id: -1,
            firstName: 'Without',
            lastName: 'teacher'
          } as User,
          ...value.content
        ];
      });
    this.coursesService.getAll(0, 9999).subscribe({
      next: (response: Page<any>) => {
        this.courses = [
          { id: '', name: 'All courses' }
          , ...response.content
        ];
      },
      error: (error) => {
        this.errorMessage = error.error.error;
      },
    });
    this.teachersFormGroup.controls.teacherId.valueChanges.subscribe(
      (teacherId) => {
        delete this.filters.teacher;
        if (teacherId !== '') {
          this.filters.teacher = `teacherId=${teacherId}`;
        }
        this.checkRangeAndGetBookedLessons();
      }
    );
    this.refreshSchedules();
    this.bundlesFormGroup.controls.bundleId.valueChanges.subscribe(
      (bundleId) => {
        delete this.filters.bundle;
        if (bundleId !== '') {
          this.filters.bundle = `bundleId=${bundleId}`;
        }
        this.checkRangeAndGetBookedLessons();
      }
    );
    this.schedulesFormGroup.controls.scheduleId.valueChanges.subscribe(
      (scheduleId) => {
        delete this.filters.schedule;
        if (scheduleId !== '') {
          this.filters.schedule = `scheduleId=${scheduleId}`;
        }
        this.checkRangeAndGetBookedLessons();
      }
    );
  }

  refreshSchedules(): void {
    delete this.filters.scheduleId;
    this.scheduleService.getScheduledLessonsByDateRange(
      this.formatter.format(this.fromDate),
      this.formatter.format(this.toDate),
      false
    ).subscribe((response) => {
      this.schedules = [
        { id: '', name: 'All Lessons' },
        ...response
      ];
      this.schedulesFormGroup.controls.scheduleId.setValue('');
    });
  }

  get radioModel(): string {
    return this.radioModelValue;
  }

  set radioModel(val: string) {
    if (val !== this.radioModelValue) {
      this.radioModelValue = val;
      this.onRadioChange();
    }
  }

  onDateSelection(date: NgbDate): void {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
      this.datepicker.markDisabled(date);
    } else if (this.fromDate && !this.toDate && date && date.equals(this.fromDate)) {
      this.fromDate = null;
    } else if (this.fromDate && !this.toDate && date && date.after(this.fromDate)) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  isHovered(date: NgbDate): boolean {
    return (
      this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate): boolean {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate): boolean {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input);
    return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue;
  }

  markDisabled = (date: NgbDate): boolean => {
    // if (!this.fromDate) {
    return false;
    /*}
    return date.before(this.calendar.getPrev(this.fromDate, 'd', 30)) ||
      date.after(this.calendar.getNext(this.fromDate, 'd', 30));*/
  }

  isDisabled = (date: NgbDate): boolean => {
    // if (!this.fromDate) {
    return false;
    // }
    // return date.before(this.calendar.getPrev(this.fromDate, 'd', 30)) ||
    //   date.after(this.calendar.getNext(this.fromDate, 'd', 30));
  }

  getFromToDate(fromDate, toDate): string {
    if (fromDate === null || toDate === null) {
      return '';
    }
    return this.formatter.format(fromDate) + ' - ' + this.formatter.format(toDate);
  }

  checkRangeAndGetBookedLessons(): void {
    this.datepicker?.close();
    this.bookedLessonsService
      .getBookedLessonsByDateRange(
        this.formatter.format(this.fromDate),
        this.formatter.format(this.toDate),
        this.pageNumber - 1,
        this.pageSize,
        Object.values(this.filters),
      ).subscribe((response: Page<any>) => {
      const grouped = response.content.reduce((acc, item) => {
        const index = `${item.scheduleId}-${item.bookingDate}`;
        if (!acc[index]) {
          acc[index] = { participantsInfo: [] };
        }
        const participants = item.participantsInfo.map((participant) => {
          return {
            bookingDate: item.bookingDate,
            bookingId: item.bookingId,
            name: item.name,
            startTime: item.startTime,
            ...participant
          };
        });
        acc[index].participantsInfo.push(...participants);
        acc[index].bookingDate = item.bookingDate;
        acc[index].name = item.name;
        acc[index].teacherFirstName = item.teacherFirstName;
        acc[index].teacherLastName = item.teacherLastName;
        acc[index].startTime = item.startTime;
        acc[index].endTime = item.endTime;
        return acc;
      }, {});
      this.selectedLessons = [...Object.values(grouped)];
      this.totalElements = response.totalElements;
    });
  }

  clearSelection(): void {
    this.fromDate = null;
    this.toDate = null;
  }

  openAddBookingModal(): void {
    this.currentDialog = this.modalService.open(BookedLessonAddModal, {
      scrollable: true,
      windowClass: 'modal-add-booking',
      size: 'lg',
      centered: true,
    });

    this.currentDialog.result.then(
      () => {
      },
      (error) => {
        this.errorMessage = error.error.message;
      }
    );
  }

  toggleRow(index: number): void {
    this.expandedRowIndex = this.expandedRowIndex === index ? null : index;
  }

  handlePageChange(event): void {
    this.pageNumber = event;
    this.checkRangeAndGetBookedLessons();
  }

  handlePageSizeChange(event): void {
    this.pageSize = event.target.value;
    this.pageNumber = 1;
    this.checkRangeAndGetBookedLessons();
  }

  getMinLimit(): number {
    return this.pageSize * (this.pageNumber - 1) + 1;
  }

  getMaxLimit(): number {
    const maxLimit = this.pageSize * this.pageNumber;
    return maxLimit <= this.totalElements ? maxLimit : this.totalElements;
  }

  onRadioChange(): void {
    this.pageNumber = 1;
    this.filters = {
      subscriptionType: `type=${this.radioModelValue}`,
    };
    this.teachersFormGroup.controls.teacherId.setValue('');
    this.bundlesFormGroup.controls.bundleId.setValue('');
    this.schedulesFormGroup.controls.scheduleId.setValue('');
  }

  cancelLesson($event: any, lesson: any): void {
    $event.stopPropagation();
    $event.preventDefault();

    const ngbModalRef = this.modalService.open(ConfirmDialogComponent, {
      scrollable: true,
      windowClass: 'confirm-window',
      size: 'md',
      centered: true,
      backdrop: false,
    });

    ngbModalRef.componentInstance.message =
      `Are you sure you want to cancel the lesson
      <b style="white-space: nowrap">${lesson.name}</b> scheduled for
      <b>${lesson.bookingDate}, ${lesson.startTime.slice(0, -3)}</b>?
    `;
    ngbModalRef.componentInstance.confirmButtonMessage = 'Yes';
    ngbModalRef.componentInstance.confirmationMessageRequired = true;

    ngbModalRef.result.then((result) => {
      if (result.status === 'confirmed') {
        if (lesson.participantsInfo) {
          const bookingIds = lesson.participantsInfo.map(pI => pI.bookingId);
          const queries = bookingIds.map(id => {
            return this.bookedLessonsService.cancelLesson(id, result.confirmationMessage);
          });
          forkJoin(queries).subscribe(r => {
            this.checkRangeAndGetBookedLessons();
            this.expandedRowIndex = null;
          });
        } else {
          this.bookedLessonsService.cancelLesson(lesson.bookingId, result.confirmationMessage).subscribe((r) => {
            console.log(r);
            this.expandedRowIndex = null;
            this.checkRangeAndGetBookedLessons();
          });
        }
      }
    });
  }

  rescheduleLesson($event: any, lesson: any): void {
    $event.stopPropagation();
    $event.preventDefault();

    const ngbModalRef = this.modalService.open(RescheduleBookedLessonComponent, {
      scrollable: true,
      windowClass: 'confirm-window',
      size: 'md',
      centered: true,
      backdrop: false,
    });

    ngbModalRef.componentInstance.bookedLesson = lesson;

    ngbModalRef.result.then((result) => {
      if (result === 'success') {
        this.checkRangeAndGetBookedLessons();
      }
    });
  }
}
